import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { useIsScreen } from '@/app/hooks';
import type { ProjectDetailType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { useGetProjectList } from '../../../apis';
import { CreateCard } from './CreateCard';
import { ProjectCard } from './ProjectCard';

const SCROLL_CONTAINER_ID = 'project-list-scroll-container';

export const ProjectList = ({ checkCanCreateProject }: { checkCanCreateProject: () => Promise<boolean> }) => {
  const t = useTranslations();
  const router = useRouter();
  const [projectList, setProjectList] = useState<ProjectDetailType[]>([]);
  const pageRef = useRef<number>(0);
  const [openProjectListDialog, setOpenProjectListDialog] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isSmScreen, isMdScreen, isLgScreen, isXlScreen } = useIsScreen();

  // 获取项目列表
  const { fetchProjectListAsync, total } = useGetProjectList();

  // 计算应该展示的项目数量（减去 1 个 CreateCard）
  const maxProjectsToShow = useMemo(() => {
    const getColumnsPerRow = () => {
      if (isSmScreen) return 2;
      if (isMdScreen) return 3;
      if (isLgScreen) return 4;
      if (isXlScreen) return 6;
      return 5; // PC 端默认
    };
    return getColumnsPerRow() - 1; // 减去 CreateCard 占用的 1 列
  }, [isSmScreen, isMdScreen, isLgScreen, isXlScreen]);

  // 初始化数据
  useEffect(() => {
    fetchProjectList(pageRef.current);
  }, []);

  const fetchProjectList = (page: number) => {
    fetchProjectListAsync(page).then(({ data }) => {
      if (data) {
        setProjectList((prev) => prev.concat(data.projects || []));
        pageRef.current++;
      }
    });
  };

  const handleDemoClick = useCallback((projectId: string) => {
    router.push(`/project/${projectId}`);
  }, []);

  const handleCreateProject = async () => {
    reportEvent('project_create_click', {
      element_name: 'create_project',
    });
    if (await checkCanCreateProject()) {
      router.push('/project/new');
    }
  };

  const loadMoreProjects = useCallback(() => {
    fetchProjectList(pageRef.current);
  }, []);

  // 检查是否需要加载更多内容（当内容未填满容器时）
  const checkAndLoadMore = useCallback(() => {
    const container = document.getElementById(SCROLL_CONTAINER_ID);
    if (!container) return;

    const { scrollHeight, clientHeight } = container;
    const hasMore = projectList.length < total;

    // 如果内容未超出容器且还有更多数据，主动触发加载
    if (scrollHeight <= clientHeight && hasMore) {
      loadMoreProjects();
    }
  }, [projectList.length, total, loadMoreProjects]);

  // 监听 Dialog 打开状态
  useEffect(() => {
    if (openProjectListDialog && projectList.length > 0) {
      // 延迟检查，确保 DOM 完全渲染和动画完成
      const timer = setTimeout(() => {
        checkAndLoadMore();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [openProjectListDialog, projectList.length, checkAndLoadMore]);

  // 监听项目列表变化，持续检查是否需要加载更多
  useEffect(() => {
    if (openProjectListDialog && projectList.length > 0 && projectList.length < total) {
      const timer = setTimeout(() => {
        checkAndLoadMore();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [projectList.length, openProjectListDialog, total, checkAndLoadMore]);

  return (
    <>
      <div className="w-auto pt-8 pb-4">
        <div className="flex items-start justify-between">
          <div className="font-medium text-system-white text-xl">{t('agentProject.myProject.title')}</div>
          {projectList.length > maxProjectsToShow && (
            <div
              className="cursor-pointer font-normal text-sm text-system-white36 hover:text-system-white64"
              onClick={() => setOpenProjectListDialog(true)}
            >
              {t('agentProject.myProject.seeAll')}
            </div>
          )}
        </div>
        <div className="mt-4 grid w-full grid-cols-5 items-center gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          <CreateCard onClick={handleCreateProject} />
          {projectList.slice(0, maxProjectsToShow).map((project, index) => {
            return <ProjectCard key={project.id} project={project} index={index} onClick={handleDemoClick} />;
          })}
        </div>
      </div>

      {/* 滚动加载哨兵元素 */}
      <Dialog open={openProjectListDialog} onOpenChange={setOpenProjectListDialog}>
        <DialogContent className="data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 !flex !flex-col top-[100px] h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] w-full max-w-full translate-y-[0] p-0 pt-8 duration-300 data-[state=closed]:animate-out data-[state=open]:animate-in">
          <div className="mx-auto flex h-full w-auto flex-col">
            <div className="h-8 px-3 font-medium text-system-white text-xl">
              <div>{t('agentProject.myProject.allProjects')}</div>
            </div>
            {/* 无限滚动容器 */}
            <div id={SCROLL_CONTAINER_ID} ref={scrollContainerRef} className="min-h-0 flex-1 overflow-auto">
              <InfiniteScroll
                dataLength={projectList.length}
                next={loadMoreProjects}
                hasMore={projectList.length < total}
                loader={
                  <div className="flex w-full items-center justify-center py-4">
                    <p className="text-system-white64">{t('agentProject.loading')}</p>
                  </div>
                }
                endMessage={
                  <div className="flex w-full items-center justify-center py-4">
                    <p className="text-system-white64">{t('agentProject.noMore')}</p>
                  </div>
                }
                scrollableTarget={SCROLL_CONTAINER_ID}
                scrollThreshold={0.9}
                className="w-full px-3 pb-10"
              >
                <div className="mt-10 grid w-full grid-cols-5 items-center gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                  <CreateCard onClick={handleCreateProject} />
                  {projectList.map((project, index) => {
                    return <ProjectCard key={project.id} project={project} index={index} onClick={handleDemoClick} />;
                  })}
                </div>
              </InfiniteScroll>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
